import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "./ui/Toast";

export interface MenuEintrag {
  id: string;
  label: string;
  /** Trennlinie oberhalb dieses Eintrags. */
  trenner?: boolean;
  gefahr?: boolean;
  /** Klappt eine Unterliste auf, statt sofort zu handeln. */
  unterliste?: { id: string; label: string }[];
  aktion?: () => void | Promise<void>;
}

/**
 * Kontextmenü an der Zeigerposition. Bewusst eigenständig gehalten: Es muss
 * über allem liegen, darf nicht am Rand herausragen und schließt bei jedem
 * Klick außerhalb — drei Dinge, die man sonst überall neu baut.
 *
 * Es hängt an document.body, nicht an der Liste. Sonst reicht "position: fixed"
 * nicht: Die umgebende Pane hat einen backdrop-filter, und ein Filter macht
 * jedes Element zum Bezugsrahmen für fixierte Kinder. Die Viewport-Koordinaten
 * des Klicks wären dann gegen die Pane gerechnet — das Menü säße daneben und
 * würde am "overflow: hidden" der Pane abgeschnitten.
 *
 * Tastatur: Der Fokus springt beim Öffnen auf den ersten Eintrag. Pfeiltasten,
 * Pos1/Ende bewegen ihn, Pfeil rechts/links klappt eine Unterliste auf und zu,
 * Esc und Tab schließen und geben den Fokus zurück.
 */
export function KontextMenue({
  x, y, eintraege, onClose, onUnterwahl,
}: {
  x: number;
  y: number;
  eintraege: MenuEintrag[];
  onClose: () => void;
  onUnterwahl: (eintragId: string, wertId: string) => void;
}) {
  const box = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState({ left: x, top: y });
  const [offen, setOffen] = useState<string | null>(null);
  const vorher = useRef<HTMLElement | null>(null);

  // Schließen und den Fokus dorthin zurückgeben, wo er vor dem Öffnen war.
  const schliessen = useCallback((fokusZurueck = true) => {
    onClose();
    if (fokusZurueck && vorher.current && document.contains(vorher.current)) vorher.current.focus({ preventScroll: true });
  }, [onClose]);

  // Vor dem Zeichnen umlegen, wenn das Menü sonst aus dem Fenster ragt.
  const einpassen = useCallback(() => {
    const el = box.current;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    setPos({
      left: x + width > window.innerWidth - 8 ? Math.max(8, x - width) : x,
      top: y + height > window.innerHeight - 8 ? Math.max(8, window.innerHeight - height - 8) : y,
    });
  }, [x, y]);

  useLayoutEffect(einpassen, [einpassen, offen]);

  useEffect(() => {
    vorher.current = document.activeElement as HTMLElement | null;
    box.current?.querySelector<HTMLElement>('[role="menuitem"]:not([aria-disabled="true"])')?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    const zu = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) schliessen(false);
    };
    const taste = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); schliessen(); }
      else if (e.key === "Tab") { e.preventDefault(); schliessen(); }
    };

    // Eine Größenänderung lässt scrollbare Container nachrücken und löst dabei
    // selbst ein scroll-Ereignis aus. Das ist kein Scrollen des Nutzers und
    // darf das Menü nicht schließen — sonst verschwindet es beim Drehen des
    // Tablets, beim Einblenden der Bildschirmtastatur oder beim Zoomen.
    let letzteGroessenaenderung = 0;
    const gescrollt = (e: Event) => {
      // Scrollen in der eigenen Unterliste ist kein Grund zu schließen.
      if (box.current?.contains(e.target as Node)) return;
      if (performance.now() - letzteGroessenaenderung > 250) schliessen(false);
    };
    const groesse = () => {
      letzteGroessenaenderung = performance.now();
      einpassen();
    };

    // In der Erfassungsphase, damit ein Klick woanders nicht erst dort wirkt.
    document.addEventListener("mousedown", zu, true);
    document.addEventListener("keydown", taste, true);
    // Beim Scrollen schließen — das Menü gehört zu einer Zeile, die wegwandert.
    // Bei Größenänderung nur neu einpassen: Die gewählte Nachricht steht fest,
    // und ein Menü, das beim Fenstergrößeändern verschwindet, ist ärgerlich.
    window.addEventListener("resize", groesse);
    window.addEventListener("scroll", gescrollt, true);
    return () => {
      document.removeEventListener("mousedown", zu, true);
      document.removeEventListener("keydown", taste, true);
      window.removeEventListener("resize", groesse);
      window.removeEventListener("scroll", gescrollt, true);
    };
  }, [schliessen, einpassen]);

  const pfeile = (e: React.KeyboardEvent) => {
    const liste = [...(box.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])];
    const i = liste.indexOf(document.activeElement as HTMLElement);
    const fokus = (n: number) => { e.preventDefault(); liste[(n + liste.length) % liste.length]?.focus(); };
    switch (e.key) {
      case "ArrowDown": fokus(i + 1); break;
      case "ArrowUp": fokus(i - 1); break;
      case "Home": fokus(0); break;
      case "End": fokus(liste.length - 1); break;
      case "ArrowRight": {
        const id = (document.activeElement as HTMLElement | null)?.dataset.unterliste;
        if (id && offen !== id) {
          e.preventDefault();
          setOffen(id);
          // Nach dem Aufklappen auf den ersten Untereintrag.
          requestAnimationFrame(() => box.current?.querySelector<HTMLElement>(".menue-unterliste [role=\"menuitem\"]")?.focus());
        }
        break;
      }
      case "ArrowLeft":
        if (offen) {
          e.preventDefault();
          box.current?.querySelector<HTMLElement>(`[data-unterliste="${CSS.escape(offen)}"]`)?.focus();
          setOffen(null);
        }
        break;
    }
  };

  return createPortal(
    <div ref={box} className="kontextmenue" style={pos} role="menu" onKeyDown={pfeile} onContextMenu={(e) => e.preventDefault()}>
      {eintraege.map((e) => (
        <div key={e.id} role="none">
          {e.trenner && <div className="menue-trenner" role="separator" />}
          <button
            type="button"
            className="menue-eintrag"
            data-gefahr={e.gefahr}
            data-offen={offen === e.id}
            data-unterliste={e.unterliste ? e.id : undefined}
            role="menuitem"
            aria-haspopup={e.unterliste ? "menu" : undefined}
            aria-expanded={e.unterliste ? offen === e.id : undefined}
            aria-disabled={!e.unterliste && !e.aktion ? "true" : undefined}
            tabIndex={-1}
            onClick={async () => {
              if (e.unterliste) return setOffen(offen === e.id ? null : e.id);
              if (!e.aktion) return;
              schliessen();
              try {
                await e.aktion();
              } catch (err) {
                toast.fehler(err);
              }
            }}
          >
            <span className="menue-label">{e.label}</span>
            {e.unterliste && (
              offen === e.id
                ? <ChevronDown className="menue-pfeil" aria-hidden="true" />
                : <ChevronRight className="menue-pfeil" aria-hidden="true" />
            )}
          </button>

          {e.unterliste && offen === e.id && (
            <div className="menue-unterliste" role="menu" aria-label={e.label}>
              {e.unterliste.length === 0 && <div className="menue-leer">Keine Einträge</div>}
              {e.unterliste.map((u) => (
                <button
                  type="button"
                  key={u.id}
                  className="menue-eintrag klein"
                  role="menuitem"
                  tabIndex={-1}
                  title={u.label}
                  onClick={() => { schliessen(); onUnterwahl(e.id, u.id); }}
                >
                  <span className="menue-label">{u.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>,
    document.body,
  );
}
